<template>
  <div class="pathway-step-preview">
    <h6 class="pathway-step-preview__title">
      Step #{{ number }}
    </h6>
    <div class="pathway-step-preview__content-status">
      <div class="pathway-step-preview__content-status__items">
        <a v-for="tool in tools"
           :key="tool.toolId"
           :href="getToolQuickLink( tool )"
           :title="tool.workItem.toolName"
           class="pathway-step-preview__content-status__tool
             pathway-step-preview__content-status__item"
           :class="classes.topic.status[tool.status]">
          <template v-if="tool.status === TOPIC_STATUS.NOT_STARTED">
            <font-awesome-icon :icon="['fal', 'tools']"></font-awesome-icon>
          </template>
          <template v-else-if="tool.status === TOPIC_STATUS.STARTED">
            <font-awesome-icon icon="hourglass-start"></font-awesome-icon>
          </template>
          <template v-else-if="tool.status === TOPIC_STATUS.COMPLETED">
            <font-awesome-icon icon="check"></font-awesome-icon>
          </template>
          <template v-else-if="tool.status === TOPIC_STATUS.MASTERED">
            <font-awesome-icon icon="award"></font-awesome-icon>
          </template>
          <template v-else>
            ?
          </template>
        </a>

        <a v-for="topic in topics"
           :key="topic.topicId"
           :href="getTopicQuickLink( topic )"
           :title="topic.workItem.topicName"
           class="pathway-step-preview__content-status__topic
             pathway-step-preview__content-status__item"
           :class="classes.topic.status[topic.status]">
          <template v-if="topic.status === TOPIC_STATUS.NOT_STARTED">
            <font-awesome-icon :icon="['fal', 'file-alt']"></font-awesome-icon>
          </template>
          <template v-else-if="topic.status === TOPIC_STATUS.STARTED">
            <font-awesome-icon icon="hourglass-start"></font-awesome-icon>
          </template>
          <template v-else-if="topic.status === TOPIC_STATUS.COMPLETED">
            <font-awesome-icon icon="check"></font-awesome-icon>
          </template>
          <template v-else-if="topic.status === TOPIC_STATUS.MASTERED">
            <font-awesome-icon icon="award"></font-awesome-icon>
          </template>
          <template v-else>
            ?
          </template>
        </a>
      </div>
    </div>
  </div>
</template>
<script>

import { TOPIC_STATUS } from "~/Components/constants";

import {
  listUserTopics,
} from "~/Scripts/resources/topicResource";

const baseUrl = "/umbraco/surface/PathwayStepSurface/QuickLink";

export default {
  name: "",
  components: {

  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    number: {
      type: Number,
      default: undefined,
    },
    userTopics: {
      /*
        To prevent multiple network calls and improve efficiency it is recommended that
        userTopics are supplied though the props when using the component within a loop.
        **The user topics are required to render progression**
      */
      type: Array,
      default: () => undefined,
    },
  },
  data: () => ( {
    TOPIC_STATUS,
    isLoggedIn: false,
    isLoading: {
      main: false,
      topics: {
        user: {
          status: true,
        },
      },
      tools: {
        user: {
          status: true,
        },
      },
    },
    classes: {
      topic: {
        status: {
          [TOPIC_STATUS.NOT_STARTED]: "pathway-step-preview__content-status__topic--not-started",
          [TOPIC_STATUS.STARTED]: "pathway-step-preview__content-status__topic--started",
          [TOPIC_STATUS.COMPLETED]: "pathway-step-preview__content-status__topic--completed",
          [TOPIC_STATUS.MASTERED]: "pathway-step-preview__content-status__topic--mastered",
        },
      },
    },
  } ),
  asyncComputed: {
    topicStatusCollection: {
      lazy: true,
      async get () {
        try {
          const { status, data: userTopics } = this.userTopics
            ? { status: 200, data: this.userTopics } : await listUserTopics();

          this.isLoading.topics.user.status = false;

          if ( status === 200 && userTopics ) {
            const kvpTopicStatus = userTopics.map(
              topic => ( { [topic.topicId]: topic.topicUserStatusSystemCode } ),
            );

            return Promise.resolve( kvpTopicStatus );
          }

          if ( status === 401 ) {
            this.isLoggedIn = false;
            console.warn( "The user is not authorised." );
            return Promise.reject( new Error( "Not authorised." ) );
          }

          return Promise.reject( new Error( "Internal server error" ) );
        } catch ( ex ) {
          console.error( ex );
          this.isLoading.topics.user.status = false;
          return Promise.reject( ex );
        }
      },
    },
  },
  computed: {
    topics () {
      return this.items.map( item => ( Object.prototype.hasOwnProperty.call( item.workItem, "topicId" )
        ? { ...item, status: this.getTopicStatus( item.workItem.topicId ) } : null ) )
        .filter( topic => topic !== null );
    },
    tools () {
      return this.items.map( item => ( Object.prototype.hasOwnProperty.call( item.workItem, "toolId" )
        ? {
          ...item,
          status: this.getToolStatus( item.workItem.toolPathwayUserStatus ),
        } : null ) )
        .filter( tool => tool !== null );
    },
  },
  methods: {
    getToolQuickLink ( tool ) {
      return `${baseUrl}?toolAlias=${tool.workItem.toolSystemCode}`;
    },
    getTopicQuickLink ( topic ) {
      return `${baseUrl}?topicId=${topic.workItem.topicId}`;
    },
    getToolStatus ( statusId ) {
      let result = TOPIC_STATUS.NOT_STARTED;

      if ( statusId === 1 ) {
        result = TOPIC_STATUS.STARTED;
      } else if ( statusId > 1 ) {
        result = TOPIC_STATUS.COMPLETED;
      }

      return result;
    },
    getTopicStatus ( id ) {
      if ( this.topicStatusCollection === null ) {
        return false;
      }

      return ( this.topicStatusCollection.find(
        topic => Object.prototype.hasOwnProperty.call( topic, id ) ) || {}
      )[id];
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../Styles/utils/index.less";

.pathway-step-preview {
  padding: 4px 0;
  display: flex;
  align-items: center;

  &__title {
    flex-basis: 80px;
    min-width: 80px;
    margin: 0;
  }

  &__content-status {
    display: flex;
    justify-content: flex-end;
    flex: auto;
    gap: 4px;

    &__icon {
      color: @white;
    }

    &__item {
      background: @not-started;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-grow: 0;
      flex-shrink: 0;
      flex-basis: 1.5rem;
      margin-right: 5px;
      border-radius: 100px;
      width: 1.5rem;
      height: 1.5rem;

      &:last-child {
        margin-right: 0;
      }
    }

    &__items {
      flex: auto;
      display: flex;
    }

    &__tool {
      &--not-started {
        background: @not-started;
      }

      &--started {
        background: @started;
        color: #fff;
      }

      &--completed {
        background: @completed;
        color: #fff;
      }

      &--mastered {
        background: @mastered;
        color: #fff;
      }
    }

    &__topic {
      &--not-started {
        background: @not-started;
      }

      &--started {
        background: @started;
        color: #fff;
      }

      &--completed {
        background: @completed;
        color: #fff;
      }

      &--mastered {
        background: @mastered;
        color: #fff;
      }
    }
  }
}
</style>
